import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { LoadingButton } from '@mui/lab';
import { IAsyncDialogProps } from '../AsyncDialog/AsyncDialog';

export interface ConfirmationDialogData {
  title: string | React.JSX.Element;
  description: string | React.JSX.Element;
  loading?: boolean;
  acceptButtonText?: string;
  rejectButtonText?: string;
  onConfirm?: () => Promise<void>;
  confirmButtonColor?: 'error' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';
}

export function ConfirmationDialog(props: IAsyncDialogProps<ConfirmationDialogData, boolean>) {
  const [isProcessing, setIsProcessing] = React.useState(false);

  // Reset processing state when dialog opens or closes
  React.useEffect(() => {
    if (props.open) {
      // Reset when dialog opens
      setIsProcessing(false);
    }
  }, [props.open]);

  const handleConfirm = async () => {
    if (isProcessing) return;

    // If there's a custom onConfirm handler, use it and don't close immediately
    if (props.data?.onConfirm) {
      setIsProcessing(true);
      try {
        await props.data.onConfirm();
        // Close dialog after successful operation
        props.onOk(true);
      } catch (error) {
        // Error handling is done in the parent component
        // Keep dialog open on error
        setIsProcessing(false);
      }
    } else {
      // Default behavior: close immediately
      props.onOk(true);
    }
  };

  const handleCancel = () => {
    if (!isProcessing) {
      props.onOk(false);
    }
  };

  const confirmButtonColor = props.data?.confirmButtonColor || 'error';

  return (
    <Dialog open={props.open} onClose={handleCancel} disableEscapeKeyDown={isProcessing}>
      <DialogTitle>{props.data?.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{props.data?.description}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={handleConfirm}
          loading={isProcessing}
          variant="contained"
          color={confirmButtonColor}
          disabled={isProcessing}
        >
          {props.data?.acceptButtonText || 'Yes'}
        </LoadingButton>
        <Button onClick={handleCancel} disabled={isProcessing}>
          {props.data?.rejectButtonText || 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
